import { useDailyChallenges } from "@/hooks/use-daily-challenge";
import { useGamification } from "@/hooks/use-gamification";
import { useAuth } from "@/hooks/use-auth";
import { useGamificationUIStore } from "@lyder-talk/core";
import { useGamificationToast } from "@/contexts/GamificationToastContext";

import { StreakDetailModal } from "./StreakDetailModal";

/**
 * The one place the streak dialog is mounted.
 *
 * ## Why it moved out of the routes
 *
 * `StreakDetailModal` is driven by a *global* store
 * (`useGamificationUIStore().streakModalOpen`) but it was mounted in three
 * places — `PageHeader`, `AppShell`, and `Home` — each with an identical
 * eleven-prop block. Store-driven state needs exactly one subscriber: on the
 * home page, which renders inside `PublicLayout` and therefore has both a
 * `PageHeader` and a `Home` body, **two** of the three were mounted at once
 * and two dialogs opened on top of each other. Every `Escape` press then only
 * dismissed the top one, and the one underneath swallowed the next click.
 *
 * The fix is not "pick a better mount site" — any route that renders more than
 * one of them brings the bug back. It is to mount it once, above the routes,
 * where the store has one subscriber regardless of which chrome the current
 * page happens to use. Everything it needs (`useGamification`,
 * `useDailyChallenges`) is already cached app-wide, so hoisting costs no extra
 * requests.
 */
export function StreakDetailHost() {
  const { isAuthenticated } = useAuth();
  const gamification = useGamification();
  // The dialog is the only consumer here, and it is closed on most page loads.
  // `AutoClaimRewards` is the one enabled caller at the app root, so opening
  // the dialog is a cache read rather than a second write.
  const {
    streakModalOpen,
    streakModalTab,
    setStreakModalOpen,
    setStreakModalTab,
    claimingChallengeId,
    handleClaimReward,
  } = useGamificationUIStore();
  const dailyChallenges = useDailyChallenges({ enabled: streakModalOpen });
  const { showMilestoneAchieved, showFreezeUsed } = useGamificationToast();

  if (!isAuthenticated) return null;

  return (
    <StreakDetailModal
      open={streakModalOpen}
      onOpenChange={(open) => {
        setStreakModalOpen(open);
        // Forget the requested tab once the dialog closes, so the next open
        // from the streak card starts on the calendar again.
        if (!open) setStreakModalTab(null);
      }}
      initialTab={streakModalTab ?? undefined}
      gamification={gamification.data}
      challenges={dailyChallenges.challenges}
      challengesLoading={dailyChallenges.isLoading}
      claimingId={claimingChallengeId}
      onClaimReward={(id) =>
        handleClaimReward(id, {
          challenges: dailyChallenges.challenges,
          claimReward: dailyChallenges.claimReward,
        })
      }
      onClaimMilestone={(_milestoneId, badgeName, xpBonus, freezesAwarded) => {
        showMilestoneAchieved({ badgeName, xpBonus, freezesAwarded, streakDays: 0 });
        gamification.refetch();
      }}
      onFreezeUsed={(message) => {
        showFreezeUsed({
          message,
          currentStreak: gamification.currentStreak,
          freezesRemaining: gamification.freezesAvailable,
        });
        gamification.refetch();
      }}
    />
  );
}

export default StreakDetailHost;